import type { Rect } from "./geometry";

/** annotation_canvas.py MIN_ZOOM / MAX_ZOOM / WHEEL_STEP */
export const MIN_ZOOM = 0.02;
export const MAX_ZOOM = 40;
export const WHEEL_STEP = 1.15;

/**
 * The view onto the image: one scale and an offset. The scene is the image, in pixels, as in the
 * desktop's QGraphicsScene; like it, the view cannot be scrolled off the image, and an image smaller
 * than the view is centred.
 */
export class Viewport {
  scale = 1;
  tx = 0;
  ty = 0;
  w = 0;
  h = 0;
  iw = 0;
  ih = 0;

  setImage(iw: number, ih: number) { this.iw = iw; this.ih = ih; }
  setSize(w: number, h: number) { this.w = w; this.h = h; this.constrain(); }

  toScene(sx: number, sy: number): [number, number] { return [(sx - this.tx) / this.scale, (sy - this.ty) / this.scale]; }
  toScreen(x: number, y: number): [number, number] { return [this.tx + x * this.scale, this.ty + y * this.scale]; }

  /** QGraphicsView.fitInView(KeepAspectRatio): the largest scale that shows `r`, centred on it. */
  fit(r: Rect = { x1: 0, y1: 0, x2: this.iw, y2: this.ih }) {
    const bw = Math.max(r.x2 - r.x1, 1e-6), bh = Math.max(r.y2 - r.y1, 1e-6);
    if (this.w <= 0 || this.h <= 0) return;
    this.scale = clampZoom(Math.min(this.w / bw, this.h / bh));
    this.tx = this.w / 2 - ((r.x1 + r.x2) / 2) * this.scale;
    this.ty = this.h / 2 - ((r.y1 + r.y2) / 2) * this.scale;
    this.constrain();
  }

  /** AnnotationCanvas.zoom_by: clamp the resulting zoom, keep the scene point under the anchor in place. */
  zoomBy(factor: number, anchor: [number, number] = [this.w / 2, this.h / 2]): boolean {
    if (this.scale <= 0 || factor <= 0) return false;
    const target = clampZoom(this.scale * factor);
    if (Math.abs(target / this.scale - 1) < 1e-9) return false;
    const [sx, sy] = this.toScene(anchor[0], anchor[1]);
    this.scale = target;
    this.tx = anchor[0] - sx * target;
    this.ty = anchor[1] - sy * target;
    this.constrain();
    return true;
  }

  panBy(dx: number, dy: number) { this.tx += dx; this.ty += dy; this.constrain(); }

  /** Scroll limits of a view whose scene rect is the image. */
  constrain() {
    const fix = (t: number, size: number, view: number) => {
      const scaled = size * this.scale;
      return scaled <= view ? (view - scaled) / 2 : Math.min(0, Math.max(view - scaled, t));
    };
    this.tx = fix(this.tx, this.iw, this.w);
    this.ty = fix(this.ty, this.ih, this.h);
  }
}

export const clampZoom = (z: number) => Math.min(Math.max(z, MIN_ZOOM), MAX_ZOOM);
